"use client";

import { Button } from "@/components/ui/button";
import Link from "next/link";
import { Typography } from "@/components/ui/typography";
import type { ServicesEnsureComplianceProps } from "./types";
import { servicesEnsureComplianceMap } from "./constants";
import { memo } from "react";
import { cn } from "@/lib/utils";
import Image from "next/image";

const ServicesEnsureCompliance = ({
  variant = "notarized",
  className,
}: ServicesEnsureComplianceProps) => {
  const {
    title,
    description,
    imageUrl,
    imageDimensions,
    primaryButton,
    secondaryButton,
  } = servicesEnsureComplianceMap[variant];

  return (
    <section className={cn("section bg-background-secondary", className)}>
      <div className="container mx-auto pt-6 pb-12 lg:py-20">
        <div className="grid grid-cols-1 gap-6 place-content-start md:grid-cols-[minmax(0,0.46fr)_minmax(0,0.54fr)] md:gap-[min(4rem,4.4vw)]">
          <div className="flex">
            <div className="flex-auto min-w-0 rounded-3xl lg:rounded-xl overflow-hidden inline-flex">
              <Image
                src={imageUrl}
                width={imageDimensions?.width}
                height={imageDimensions?.height}
                alt={title}
                className="flex-auto min-w-0"
              />
            </div>
          </div>
          <div>
            <div className="grid grid-cols-1 gap-4 lg:gap-6 text-semantic-text-primary">
              <Typography variant="titleLg" className="lg:text-gray-800">
                <h3>{title}</h3>
              </Typography>
              <div className="text-base leading-[160%] lg:text-gray-600 lg:leading-[150%] ">
                <p>{description}</p>
              </div>
            </div>
            <div className="mt-6 grid grid-cols-1 gap-4 lg:inline-grid lg:grid-cols-2 lg:gap-2.5">
              {!!primaryButton && (
                <Button
                  asChild
                  variant="primary"
                  className="lg:only:col-span-2 h-[48px] !px-6"
                >
                  <Link href={primaryButton.href} className="!text-base w-full">
                    {primaryButton.label}
                  </Link>
                </Button>
              )}
              {!!secondaryButton && (
                <Button
                  asChild
                  variant="primary-outline"
                  className="lg:only:col-span-2 h-[48px] !px-6"
                >
                  <Link
                    href={secondaryButton?.href}
                    className="!text-base w-full"
                  >
                    {secondaryButton?.label}
                  </Link>
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default memo(ServicesEnsureCompliance);
